import React, { useState, useEffect, useCallback } from "react";
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Button, 
  Modal, 
  Snackbar, 
  Alert,
  LinearProgress
} from "@mui/material";
import SettingsIcon from '@mui/icons-material/Settings';

import FileUploadForm from "../components/FileUploadForm";
import ProcessingQueue from "../components/ProcessingQueue";
import HistoryTable from "../components/HistoryTable";
import OperationManager from "../components/OperationManager";
import PreviewModal from "../components/PreviewModal";
import * as api from "../services/api";

const TextProcessorView = ({ latestWebSocketMessage, isConnected }) => {
  const [operation, setOperation] = useState("");
  const [operations, setOperations] = useState([]);
  const [queue, setQueue] = useState([]);
  const [isQueuePaused, setIsQueuePaused] = useState(false);
  const [progress, setProgress] = useState({});
  const [results, setResults] = useState({});
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Modal states
  const [manageOpsOpen, setManageOpsOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [previewFilename, setPreviewFilename] = useState("");

  // Cargar datos iniciales
  useEffect(() => {
    fetchOperations();
    fetchQueue();
    fetchHistory();
  }, []);

  // Fetch Operations
  const fetchOperations = async () => {
    setLoadingOperations(true);
    try {
      const data = await api.fetchOperations();
      setOperations(data);
      if (data.length > 0 && !operation) {
        setOperation(data[0].name);
      }
    } catch (error) {
      console.error("Error al obtener operaciones:", error);
      setError("Error al cargar las operaciones");
    } finally {
      setLoadingOperations(false);
    }
  };

  // Fetch Queue
  const fetchQueue = async () => {
    setLoadingQueue(true);
    try {
      const data = await api.fetchQueue();
      setQueue(data);
    } catch (error) {
      console.error("Error al obtener cola:", error);
      setError("Error al cargar la cola de procesamiento");
    } finally {
      setLoadingQueue(false);
    }
  };

  // Fetch History
  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await api.fetchHistory();
      setHistory(data);
    } catch (error) {
      console.error("Error al obtener historial:", error);
      setError("Error al cargar el historial de procesamiento");
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Fetch Result
  const fetchResult = async (filename) => {
    try {
      const data = await api.fetchResult(filename);
      setResults((prev) => ({ ...prev, [filename]: data }));
    } catch (error) {
      console.error("Error al obtener resultado:", error);
      setError("Error al cargar el resultado");
    }
  };

  // Pause/Resume Queue
  const handlePauseResume = async () => {
    try {
      if (isQueuePaused) {
        await api.resumeQueue();
        setSuccessMessage("Cola reanudada");
      } else {
        await api.pauseQueue();
        setSuccessMessage("Cola pausada");
      }
      setIsQueuePaused(!isQueuePaused);
      fetchQueue();
    } catch (error) {
      console.error("Error al pausar/reanudar cola:", error);
      setError("Error al pausar/reanudar la cola");
    }
  };

  // Add Operation
  const handleAddOperation = async (newOperation) => {
    try {
      await api.addOperation(newOperation);
      fetchOperations();
      setSuccessMessage("Operación añadida correctamente");
    } catch (error) {
      console.error("Error al añadir operación:", error);
      throw error;
    }
  };

  // Update Operation
  const handleUpdateOperation = async (name, updatedOperation) => {
    try {
      await api.updateOperation(name, updatedOperation);
      fetchOperations();
      setSuccessMessage("Operación actualizada correctamente");
    } catch (error) {
      console.error("Error al actualizar operación:", error);
      throw error;
    }
  };

  // Delete Operation
  const handleDeleteOperation = async (name) => {
    try {
      await api.deleteOperation(name);
      fetchOperations();
      setSuccessMessage("Operación eliminada correctamente");
    } catch (error) {
      console.error("Error al eliminar operación:", error);
      throw error;
    }
  };

  // Preview Result
  const handlePreviewClick = async (filename) => {
    try {
      const response = await api.fetchResult(filename);
      setPreviewContent(response.result);
      setPreviewFilename(filename);
      setPreviewOpen(true);
    } catch (error) {
      console.error("Error al obtener vista previa:", error);
      setError("Error al cargar la vista previa");
    }
  };

  // Calculate estimated time remaining
  const calculateEstimatedTimeRemaining = (
    processedGroups,
    totalGroups,
    lastUpdateTime,
    last_last_update
  ) => {
    if (!lastUpdateTime || !last_last_update || processedGroups === 0)
      return "Calculando...";

    const currentTime = Date.now();
    const elapsedTime = currentTime - last_last_update;
    const percentageCompleted = processedGroups / totalGroups;
    const percentageRemaining = 1 - percentageCompleted;

    // Calculate the change in percentage per second
    const percentageChangePerSecond =
      percentageCompleted / (elapsedTime / 1000);

    // Estimate the remaining time in seconds
    const estimatedRemainingSeconds =
      percentageRemaining / percentageChangePerSecond;

    // Convert seconds to minutes
    const estimatedRemainingMinutes = Math.round(
      estimatedRemainingSeconds / 60
    );

    return estimatedRemainingMinutes > 0
      ? `${estimatedRemainingMinutes} minuto${estimatedRemainingMinutes !== 1 ? "s" : ""}`
      : "Menos de un minuto";
  };

  // React a mensajes WebSocket
  useEffect(() => {
    if (latestWebSocketMessage) {
      if (latestWebSocketMessage.type === "progress") {
        setProgress((prev) => {
          const currentTime = Date.now();
          return {
            ...prev,
            [latestWebSocketMessage.filename]: {
              ...latestWebSocketMessage,
              lastUpdateTime: currentTime,
              last_last_update:
                prev[latestWebSocketMessage.filename]?.processedGroups !== latestWebSocketMessage.processedGroups
                  ? prev[latestWebSocketMessage.filename]?.lastUpdateTime || currentTime
                  : prev[latestWebSocketMessage.filename]?.last_last_update || currentTime,
            },
            [latestWebSocketMessage.originalFilename]: {
              ...latestWebSocketMessage,
              lastUpdateTime: currentTime,
              last_last_update:
                prev[latestWebSocketMessage.originalFilename]?.processedGroups !==
                latestWebSocketMessage.processedGroups
                  ? prev[latestWebSocketMessage.originalFilename]?.lastUpdateTime || currentTime
                  : prev[latestWebSocketMessage.originalFilename]?.last_last_update ||
                    currentTime,
            },
          };
        });

        if (latestWebSocketMessage.isCompleted) {
          fetchResult(latestWebSocketMessage.filename);
          fetchHistory();
          setSuccessMessage(`Procesamiento completado: ${latestWebSocketMessage.originalFilename}`);
        }
      } else if (latestWebSocketMessage.type === "queueUpdate") {
        setQueue(
          latestWebSocketMessage.queue.map((item) => ({
            ...item,
            originalFilename: item.originalFilename || item.filename,
          }))
        );
      }
    }
  }, [latestWebSocketMessage, fetchHistory]);

  // Modal handlers
  const handleOpenManageOps = () => setManageOpsOpen(true);
  const handleCloseManageOps = () => setManageOpsOpen(false);
  const handleClosePreview = () => setPreviewOpen(false);
  const handleCloseSnackbar = () => setError("");
  const handleCloseSuccessSnackbar = () => setSuccessMessage("");

  // Función para limpiar el historial
  const handleClearHistory = async () => {
    try {
      await api.clearHistory();
      fetchHistory();
      setSuccessMessage("Historial limpiado correctamente");
    } catch (error) {
      console.error('Error al limpiar historial:', error);
      setError("Error al limpiar el historial");
    }
  };

  // Función para eliminar un archivo específico
  const handleDeleteItem = async (item) => {
    try {
      await api.deleteHistoryItem(item.processedFilename);
      fetchHistory();
      setSuccessMessage(`Archivo ${item.originalFilename} eliminado correctamente`);
    } catch (error) {
      console.error('Error al eliminar archivo:', error);
      setError("Error al eliminar el archivo");
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Procesador de Texto
        </Typography>
        
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={handleOpenManageOps}
        >
          Gestionar Operaciones
        </Button>
      </Box>

      {!isConnected && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Conectando al servidor WebSocket...
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Columna izquierda: Upload form y Queue */}
        <Grid item xs={12} md={5}>
          <FileUploadForm
            operations={operations}
            selectedOperation={operation}
            onOperationChange={setOperation}
            onUploadSuccess={fetchQueue}
          />
          
          {loadingQueue ? (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
            </Box>
          ) : (
            <ProcessingQueue
              queue={queue}
              progress={progress}
              isPaused={isQueuePaused}
              onPauseResume={handlePauseResume}
              calculateETA={calculateEstimatedTimeRemaining}
              onPreview={handlePreviewClick}
            />
          )}
        </Grid>

        {/* Columna derecha: History */}
        <Grid item xs={12} md={7}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Historial de Procesamiento
            </Typography>
          </Box>
          
          {loadingHistory ? (
            <LinearProgress />
          ) : (
            <HistoryTable 
              history={history} 
              onPreview={handlePreviewClick} 
              onClearHistory={handleClearHistory}
              onDeleteItem={handleDeleteItem}
              clearHistoryLabel="Limpiar historial de texto"
            />
          )}
        </Grid>
      </Grid>

      {/* Modal para gestionar operaciones */}
      <Modal open={manageOpsOpen} onClose={handleCloseManageOps}>
        <div>
          <OperationManager
            operations={operations}
            onAdd={handleAddOperation}
            onUpdate={handleUpdateOperation}
            onDelete={handleDeleteOperation}
            onClose={handleCloseManageOps}
          />
        </div>
      </Modal>

      {/* Modal para vista previa */}
      <PreviewModal
        open={previewOpen}
        onClose={handleClosePreview}
        filename={previewFilename}
        content={previewContent}
      />

      {/* Snackbar para errores */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="error" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* Snackbar para mensajes de éxito */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={handleCloseSuccessSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSuccessSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TextProcessorView; 